import { RoutesAlias } from '../routesAlias'
import { AppRouteRecord } from '@/types/router'

/**
 * 菜单列表、异步路由
 *
 * 支持两种模式:
 * 前端静态配置 - 直接使用本文件中定义的路由配置
 * 后端动态配置 - 后端返回菜单数据，前端解析生成路由
 *
 * 菜单标题（title）:
 * 可以是 i18n 的 key，也可以是字符串，比如：'用户列表'
 *
 * RoutesAlias.Layout 指向的是布局组件，后端返回的菜单数据中，component 字段需要指向 /index/index
 * 路由元数据（meta）：异步路由在 asyncRoutes 中配置，静态路由在 staticRoutes 中配置
 */
export const asyncRoutes: AppRouteRecord[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    component: RoutesAlias.Layout,
    meta: {
      title: '主页',
      icon: '&#xe721;',
      roles: ['R_SUPER', 'R_ADMIN']
    },
    children: [
      {
        path: 'console',
        name: 'Console',
        component: RoutesAlias.Dashboard,
        meta: {
          title: '首页',
          keepAlive: false,
          fixedTab: true
        }
      }
    ]
  },
  {
    path: '/dataCenter/operationalData',
    name: 'OperationalData',
    component: '/dataCenter/operationalData/index',
    meta: {
      title: '运营数据中台',
      icon: '&#xe814;'
    },
  },
  {
    path: '/storeManagement',
    name: 'StoreManagement',
    component: RoutesAlias.Layout,
    meta: {
      title: '店铺管理',
      icon: '&#xe737;'
    },
    children: [
      {
        path: '/storeManagement/storeEntry',
        name: 'StoreEntry',
        component: '/storeManagement/storeEntry/index',
        meta: {
          title: '门店录入',
          keepAlive: true
        }
      },
      {
        path: '/storeManagement/meituanChainAccount',
        name: 'MeituanChainAccount',
        component: '/storeManagement/meituanChainAccount/index',
        meta: {
          title: '美团连锁账号',
          keepAlive: true
        }
      },
      {
        path: '/storeManagement/weChatBind',
        name: 'weChatBind',
        component: '/storeManagement/weChatBind/index',
        meta: {
          title: '微信群绑定',
          keepAlive: true
        }
      },
    ]
  },
  {
    path: '/orderManagement',
    name: 'OrderManagement',
    component: RoutesAlias.Layout,
    meta: {
      title: '订单管理',
      icon: '&#xe814;'
    },
    children: [
      {
        path: '/orderManagement/orderEntry',
        name: 'OrderEntry',
        component: '/orderManagement/orderEntry/index',
        meta: {
          title: '订单列表',
          keepAlive: true
        }
      }
    ]
  },
  {
    path: '/zuoYaDesign/index',
    name: 'ZuoYaDesign',
    component: '/zuoYaDesign/index',
    meta: {
      title: '左丫设计',
      icon: '&#xe626;' // 可替换为合适的图标
    },
  },
  {
    path: '/intelligentSolution',
    name: 'IntelligentSolution',
    component: '/intelligentSolution/index',
    meta: {
      title: '智能方案',
      icon: '&#xe728;'
    },
  },
  {
    path: '/buildingStore',
    name: 'BuildingStore',
    component: RoutesAlias.Layout,
    meta: {
      title: '智能建店',
      icon: '&#xe85d;'
    },
    children: [
      {
        path: '/buildingStore/store',
        name: 'StoreIndex',
        component: '/buildingStore/store/index',
        meta: {
          title: '店铺管理',
          keepAlive: true
        }
      },
      {
        path: '/buildingStore/catering',
        name: 'Catering',
        component: '/buildingStore/catering/index',
        meta: {
          title: '餐饮智能建店',
          keepAlive: true
        }
      },
      {
        path: '/buildingStore/flashPurchase',
        name: 'FlashPurchase',
        component: '/buildingStore/flashPurchase/index',
        meta: {
          title: '闪购智能建店',
          keepAlive: true
        }
      },
    ]
  },
  {
    path: '/IntelligentOperation',
    name: 'IntelligentOperation',
    component: RoutesAlias.Layout,
    meta: {
      title: '智能运营',
      icon: '&#xe7f6;'
    },
    children: [
      {
        path: '/IntelligentOperation/weeklyReports',
        name: 'weeklyReports',
        component: '/IntelligentOperation/weeklyReports/index',
        meta: {
          title: '周报日报',
          keepAlive: true
        }
      },
      {
        path: '/IntelligentOperation/automaticMealDelivery',
        name: 'automaticMealDelivery',
        component: '/IntelligentOperation/automaticMealDelivery/index',
        meta: {
          title: '自动回评',
          keepAlive: true
        }
      },
      {
        path: '/IntelligentOperation/precisionMarket',
        name: 'precisionMarket',
        component: '/IntelligentOperation/precisionMarket/index',
        meta: {
          title: '精准营销',
          keepAlive: true
        }
      },
      {
        path: '/IntelligentOperation/storeAbnormality',
        name: 'storeAbnormality',
        component: '/IntelligentOperation/storeAbnormality/index',
        meta: {
          title: '门店异常',
          keepAlive: true
        }
      },
      {
        path: '/IntelligentOperation/operationalServices',
        name: 'operationalServices',
        component: '/IntelligentOperation/operationalServices/index',
        meta: {
          title: '运营服务',
          keepAlive: true
        }
      },
      {
        path: '/IntelligentOperation/evaluateAppeal',
        name: 'EvaluateAppeal',
        component: '/IntelligentOperation/evaluateAppeal/index',
        meta: {
          title: '评价申诉',
          keepAlive: true
        }
      },
      {
        path: '/IntelligentOperation/storePromotionPlan',
        name: 'StorePromotionPlan',
        component: '/IntelligentOperation/storePromotionPlan/index',
        meta: {
          title: '门店推广',
          keepAlive: true
        }
      },
      {
        path: '/IntelligentOperation/monthlyAnalysis',
        name: 'MonthlyAnalysis',
        component: '/IntelligentOperation/monthlyAnalysis/index',
        meta: {
          title: '月报分析',
          keepAlive: true
        }
      },
      {
        path: '/IntelligentOperation/autoOrderReady',
        name: 'AutoOrderReady',
        component: '/IntelligentOperation/autoOrderReady/index',
        meta: {
          title: '自动出餐',
          keepAlive: true
        }
      },
    ]
  },
  {
    path: '/teamManagement',
    name: 'TeamManagement',
    component: '/teamManagement/index',
    meta: {
      title: '团队管理',
      icon: '&#xe728;'
    },
    children: [
      {
        path: '/teamManagement/groupMessageReply',
        name: 'GroupMessageReply',
        component: '/teamManagement/groupMessageReply/index',
        meta: {
          title: '运营群消息回复率',
          keepAlive: true
        }
      },
      {
        path: '/teamManagement/channelManagement',
        name: 'ChannelManagement',
        component: '/teamManagement/channelManagement/index',
        meta: {
          title: '渠道管理',
          keepAlive: true
        }
      },
    ]
  },
  {
    path: '/financialManagement',
    name: 'FinancialManagement',
    component: RoutesAlias.Layout,
    meta: {
      title: '财务管理',
      icon: '&#xe728;'
    },
    children: [
      {
        path: '/financialManagement/operationStatement',
        name: 'OperationStatement',
        component: '/financialManagement/operationStatement/index',
        meta: {
          title: '代运营账单',
          keepAlive: true
        }
      },
      {
        path: '/financialManagement/serviceProvider',
        name: 'ServiceProvider',
        component: '/financialManagement/serviceProvider/index',
        meta: {
          title: '服务商账号',
          keepAlive: true
        }
      },
    ]
  },
  {
    path: '/salesCase',
    name: 'SalesCase',
    component: '/salesCase/index',
    meta: {
      title: '销售案例',
      icon: '&#xe728;'
    },
  },
  {
    path: '/authManagement',
    name: 'AuthManagement',
    component: RoutesAlias.Layout,
    meta: {
      title: '审核管理',
      icon: '&#xe728;'
    },
    children: [
      {
        path: '/system/storeReview',
        name: 'StoreReview',
        component: '/system/storeReview/index',
        meta: {
          title: '门店审核',
          keepAlive: true
        }
      },
      {
        path: '/authManagement/orders',
        name: 'AuthOrders',
        component: '/authManagement/orders/index',
        meta: {
          title: '订单审核',
          keepAlive: true
        }
      },
    ]
  },
  // {
  //   path: '/system/storeReview',
  //   name: 'StoreReview',
  //   component: '/system/storeReview/index',
  //   meta: {
  //     title: '门店审核',
  //     keepAlive: true
  //   }
  // },
  {
    path: '/system',
    name: 'System',
    component: RoutesAlias.Layout,
    meta: {
      title: '系统管理',
      icon: '&#xe7b9;',
      roles: ['R_SUPER', 'R_ADMIN']
    },
    children: [
      {
        path: 'user',
        name: 'User',
        component: '/system/user',
        meta: {
          title: '用户管理',
          keepAlive: true,
          roles: ['R_SUPER', 'R_ADMIN']
        }
      },
      {
        path: 'role',
        name: 'Role',
        component: '/system/role',
        meta: {
          title: '角色管理',
          keepAlive: true,
          roles: ['R_SUPER']
        }
      },
      {
        path: 'user-center',
        name: 'UserCenter',
        component: '/system/user-center',
        meta: {
          title: '个人中心',
          keepAlive: true,
        }
      },
      {
        path: 'menu',
        name: 'Menus',
        component: RoutesAlias.Menu,
        meta: {
          title: '菜单管理',
          keepAlive: true,
          roles: ['R_SUPER'],
          authList: [
            {
              title: '新增',
              authMark: 'add'
            },
            {
              title: '编辑',
              authMark: 'edit'
            },
            {
              title: '删除',
              authMark: 'delete'
            }
          ]
        }
      },
      {
        path: '/system/crmSetUp',
        name: 'crmSetUp',
        component: '/system/crmSetUp/index',
        meta: {
          title: '参数设置',
          keepAlive: true
        }
      },
      {
        path: '/system/intelligentSolutionSet',
        name: 'IntelligentSolutionSet',
        component: '/system/intelligentSolutionSet/index',
        meta: {
          title: '智能方案设置',
          keepAlive: true
        }
      },
      {
        path: '/system/cuisineSettings',
        name: 'CuisineSettings',
        component: '/system/cuisineSettings/index',
        meta: {
          title: '菜品设置',
          keepAlive: true
        }
      },
      {
        path: '/system/operationLog',
        name: 'OperationLog',
        component: '/system/operationLog/index',
        meta: {
          title: '操作日志',
          keepAlive: true,
        }
      },
    ]
  },
  {
    path: '/privateDomain',
    name: 'PrivateDomain',
    component: RoutesAlias.Layout,
    meta: {
      title: '私域管理',
      icon: '&#xe728;'
    },
    children: [
      {
        path: '/privateDomain/list',
        name: 'PrivateDomainList',
        component: '/privateDomain/list/index',
        meta: {
          title: '门店设置',
          keepAlive: true
        }
      },
    ]
  },
  // {
  //   path: '/exception',
  //   name: 'Exception',
  //   component: RoutesAlias.Layout,
  //   meta: {
  //     title: 'menus.exception.title',
  //     icon: '&#xe820;'
  //   },
  //   children: [
  //     {
  //       path: '403',
  //       name: '403',
  //       component: RoutesAlias.Exception403,
  //       meta: {
  //         title: 'menus.exception.forbidden',
  //         keepAlive: true
  //       }
  //     },
  //     {
  //       path: '404',
  //       name: '404',
  //       component: RoutesAlias.Exception404,
  //       meta: {
  //         title: 'menus.exception.notFound',
  //         keepAlive: true
  //       }
  //     },
  //     {
  //       path: '500',
  //       name: '500',
  //       component: RoutesAlias.Exception500,
  //       meta: {
  //         title: 'menus.exception.serverError',
  //         keepAlive: true
  //       }
  //     }
  //   ]
  // }
]
